import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Bell,
  BarChart3,
  FileText,
  UserCheck,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Wrench,
  Star,
  Eye,
  EyeOff,
  Edit3,
  Save,
  RotateCcw,
  GripVertical,
  CheckCircle,
  AlertCircle,
  MapPin,
  CreditCard,
  Server,
  Database,
  Clock,
  XCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { hasPermission, PERMISSIONS } from '../utils';
import RealtimeStatus from './RealtimeStatus';
import SessionTimer from './SessionTimer';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [localItemOrder, setLocalItemOrder] = useState<string[]>([]);
  // Initialize synchronously to avoid droppable not found errors
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return false;
  });
  const { user, logout, isLoading: authLoading } = useAuth();
  const { settings, toggleSidebar, toggleEditMode, reorderItems, resetToDefault } = useSidebar();
  const location = useLocation();

  // Update desktop state on resize
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Fetch system health
  const { data: systemHealth, isLoading: systemHealthLoading } = useQuery({
    queryKey: ['dashboard-system-health'],
    queryFn: adminAPI.getSystemHealth,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    retry: 3,
  });

  const health = systemHealth?.data || {
    server: 'healthy',
    database: 'healthy',
    apiResponseTime: 0,
    lastChecked: new Date().toISOString()
  };

  const getHealthIcon = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-3 h-3 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-3 h-3 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-3 h-3 text-red-400" />;
    }
  };

  const getResponseTimeColor = (time: number) => {
    if (time < 200) return 'text-green-400';
    if (time < 500) return 'text-yellow-400';
    return 'text-red-400';
  };


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-menu')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, category: 'Main' },
    { name: 'Users', href: '/users', icon: Users, permissions: [PERMISSIONS.USERS_READ], category: 'Main' },
    { name: 'Users Map', href: '/users-map', icon: MapPin, permissions: [PERMISSIONS.USERS_READ], category: 'Main' },
    
    { name: 'Bookings', href: '/bookings', icon: Calendar, permissions: [PERMISSIONS.BOOKINGS_READ], category: 'Operations' },
    { name: 'Manual Leads', href: '/manual-leads', icon: AlertCircle, permissions: [PERMISSIONS.BOOKINGS_WRITE], category: 'Operations' },
    { name: 'Professionals', href: '/professionals', icon: UserCheck, permissions: [PERMISSIONS.PROFESSIONALS_READ], category: 'Operations' },
    { name: 'Worker Approvals', href: '/worker-approvals', icon: UserCheck, permissions: [PERMISSIONS.PROFESSIONALS_WRITE], category: 'Operations' },
    { name: 'Hubs', href: '/hubs', icon: MapPin, permissions: [PERMISSIONS.PROFESSIONALS_WRITE], category: 'Operations' },
    
    { name: 'Services', href: '/services', icon: Wrench, permissions: [PERMISSIONS.SERVICES_READ], category: 'Services' },
    { name: 'Service Categories', href: '/service-categories', icon: Wrench, permissions: [PERMISSIONS.SERVICE_CATEGORIES_WRITE], category: 'Services' },
    { name: 'Ordering', href: '/ordering', icon: GripVertical, permissions: [PERMISSIONS.SERVICES_WRITE], category: 'Services' },
    
    { name: 'Blog Posts', href: '/blog', icon: FileText, permissions: [PERMISSIONS.BLOG_READ], category: 'Content' },
    { name: 'Blog Categories', href: '/blog/categories', icon: FileText, permissions: [PERMISSIONS.BLOG_WRITE], category: 'Content' },
    { name: 'Slideshow', href: '/slideshow', icon: FileText, permissions: [PERMISSIONS.SLIDESHOW_WRITE], category: 'Content' },
    { name: 'Coupons', href: '/coupons', icon: FileText, permissions: [PERMISSIONS.COUPONS_WRITE], category: 'Content' },
    
    { name: 'Customer Complaints', href: '/cx', icon: Users, permissions: [PERMISSIONS.SUPPORT_WRITE], category: 'System' },
    { name: 'Customer Reviews', href: '/reviews', icon: FileText, permissions: [PERMISSIONS.BLOG_READ], category: 'System' },
    { name: 'Notifications', href: '/notifications', icon: Bell, permissions: [PERMISSIONS.NOTIFICATIONS_WRITE], category: 'System' },
    { name: 'Payments', href: '/payments', icon: BarChart3, permissions: [PERMISSIONS.PAYMENTS_READ], category: 'System' },
    { name: 'Referrals', href: '/referrals', icon: Users, permissions: [PERMISSIONS.REFERRALS_WRITE], category: 'System' },
    { name: 'Audit Logs', href: '/audit', icon: FileText, permissions: [PERMISSIONS.AUDIT_READ], category: 'System' },
    { name: 'Admin Access', href: '/admin-access', icon: Users, permissions: [PERMISSIONS.ADMIN_MANAGE], category: 'System' },
  ] as { name: string; href: string; icon: any; permissions?: string[]; category: string }[];

  const isCurrentPath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Filter navigation based on user role
  // Only super-admin can see Admin Access
  // Check role with multiple variations to handle different formats
  const userRoleRaw = user?.role;
  const userRoleLower = typeof userRoleRaw === 'string' ? userRoleRaw.toLowerCase().trim() : '';
  const userRoleNoSpaces = userRoleLower.replace(/\s+/g, '-');
  const userRoleNormalized = userRoleNoSpaces.replace(/_/g, '-');

  // Check for super-admin in multiple formats
  // Also check if user exists and has any admin-like role
  const isSuperAdmin = user && (
    userRoleNormalized === 'super-admin' ||
    userRoleLower === 'superadmin' ||
    userRoleLower === 'super admin' ||
    userRoleLower === 'super-admin' ||
    userRoleRaw === 'super-admin' ||
    userRoleRaw === 'Super Admin' ||
    userRoleRaw === 'SUPER-ADMIN' ||
    userRoleRaw === 'SUPER_ADMIN' ||
    // Additional checks for variations
    (typeof userRoleRaw === 'string' && userRoleRaw.toLowerCase().includes('super') && userRoleRaw.toLowerCase().includes('admin'))
  );

  // CRITICAL DEBUG: Log everything to find the issue (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('=== LAYOUT DEBUG START ===');
      console.log('User object:', user);
      console.log('User role (raw):', userRoleRaw, typeof userRoleRaw);
      console.log('User role (lowercase):', userRoleLower);
      console.log('User role (normalized):', userRoleNormalized);
      console.log('Is Super Admin?', isSuperAdmin);
      console.log('User exists?', !!user);
      console.log('All role checks:', {
        'super-admin': userRoleNormalized === 'super-admin',
        'superadmin': userRoleLower === 'superadmin',
        'super admin': userRoleLower === 'super admin',
        rawExact: userRoleRaw === 'super-admin',
        rawCapital: userRoleRaw === 'Super Admin',
        rawUpper: userRoleRaw === 'SUPER-ADMIN',
        rawUnderscore: userRoleRaw === 'SUPER_ADMIN'
      });
      console.log('=== LAYOUT DEBUG END ===');
    }
  }, [user, userRoleRaw, userRoleLower, userRoleNormalized, isSuperAdmin]);

  // Optimized: Simplified filtering logic, removed excessive logging
  const allowedNavigation = useMemo(() => {
    if (!user) return [];

    const roleStr = userRoleRaw ? String(userRoleRaw).toLowerCase().trim() : '';
    const containsSuperAndAdmin = roleStr.includes('super') && roleStr.includes('admin');
    const isSuperAdminCheck = isSuperAdmin || containsSuperAndAdmin;

    return navigation.filter(item => {
      // Admin Access and Audit Logs are only visible to super-admins
      if (item.name === 'Admin Access' || item.name === 'Audit Logs') {
        return isSuperAdminCheck;
      }

      // Check permissions for all other items
      if (item.permissions && item.permissions.length > 0) {
        // Super-admin bypasses permission checks
        if (isSuperAdminCheck) {
          return true;
        }

        const userPermissions = user.permissions || [];
        const userPermissionsSet = new Set(userPermissions);
        return item.permissions.some(perm => userPermissionsSet.has(perm));
      }

      // If no permissions specified, show to all authenticated admins
      return true;
    });
  }, [navigation, isSuperAdmin, userRoleRaw, user]);

  // Initialize local item order when settings change
  useEffect(() => {
    setLocalItemOrder(settings.itemOrder);
  }, [settings.itemOrder]);

  // When entering edit mode, ensure sidebar is visible IMMEDIATELY
  // CRITICAL: This must happen synchronously to ensure droppable is available when drag starts
  useEffect(() => {
    if (settings.isEditMode) {
      // Force sidebar to be visible when entering edit mode
      if (isDesktop && !settings.isVisible) {
        // Auto-show sidebar when entering edit mode on desktop
        // Use requestAnimationFrame to ensure DOM update happens before drag
        requestAnimationFrame(() => {
          toggleSidebar();
        });
      }
      if (!isDesktop && !sidebarOpen) {
        // Auto-open sidebar when entering edit mode on mobile
        requestAnimationFrame(() => {
          setSidebarOpen(true);
        });
      }
    }
  }, [settings.isEditMode]);

  // Optimized: Memoize super-admin check to avoid recalculating
  const shouldShowAdminAccess = useMemo(() => {
    if (!user?.role) return false;
    const roleStr = String(user.role).toLowerCase().trim();
    return isSuperAdmin || (roleStr.includes('super') && roleStr.includes('admin'));
  }, [user?.role, isSuperAdmin]);

  // Sort navigation items according to custom order
  // Optimized: Simplified logic, removed excessive logging
  const orderedNavigation = useMemo(() => {
    if (!allowedNavigation || allowedNavigation.length === 0) {
      return [];
    }

    const savedOrder = settings.isEditMode ? localItemOrder : settings.itemOrder;

    // Step 1: Create map for O(1) lookups
    const allowedItemsMap = new Map(allowedNavigation.map(item => [item.name, item]));

    // Step 2: Build ordered list from saved order
    const orderedFromSaved: typeof navigation = [];
    const processedItems = new Set<string>();

    // Add items from saved order that are allowed
    for (const name of savedOrder) {
      const item = allowedItemsMap.get(name);
      if (item) {
        orderedFromSaved.push(item);
        processedItems.add(name);
      }
    }

    // Step 3: Add any allowed items that weren't in saved order
    for (const item of allowedNavigation) {
      if (!processedItems.has(item.name)) {
        orderedFromSaved.push(item);
        processedItems.add(item.name);
      }
    }

    // Step 4: Ensure Admin Access and Audit Logs at end for super-admins
    if (shouldShowAdminAccess) {
      const adminAccessItem = navigation.find(item => item.name === 'Admin Access');
      const auditLogsItem = navigation.find(item => item.name === 'Audit Logs');

      if (adminAccessItem && auditLogsItem) {
        const filtered = orderedFromSaved.filter(item =>
          item.name !== 'Admin Access' && item.name !== 'Audit Logs'
        );
        filtered.push(adminAccessItem);
        filtered.push(auditLogsItem);
        return filtered;
      }
    }

    return orderedFromSaved;
  }, [settings.isEditMode, localItemOrder, settings.itemOrder, allowedNavigation, shouldShowAdminAccess, navigation]);

  // Wait for auth to load before rendering navigation
  // This ensures user role is available when filtering navigation
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Handle drag end for better drag-and-drop experience
  const handleDragEnd = (result: DropResult) => {
    // Early return if no destination (drag was cancelled)
    if (!result.destination) {
      console.log('[Layout] Drag cancelled - no destination');
      return;
    }

    // Validate that the droppable exists and is one we recognize
    const validDroppableIds = ['desktop-sidebar', 'mobile-sidebar'];
    if (!validDroppableIds.includes(result.destination.droppableId)) {
      console.error('[Layout] Invalid droppableId:', result.destination.droppableId);
      console.error('[Layout] Valid droppableIds:', validDroppableIds);
      console.error('[Layout] Current state:', {
        isDesktop,
        isEditMode: settings.isEditMode,
        isVisible: settings.isVisible,
        sidebarOpen
      });
      return;
    }

    console.log('[Layout] Drag successful:', {
      droppableId: result.destination.droppableId,
      sourceIndex: result.source.index,
      destinationIndex: result.destination.index
    });

    // Ensure we have valid navigation items
    if (!orderedNavigation || orderedNavigation.length === 0) {
      console.warn('[Layout] No navigation items to reorder');
      return;
    }

    // Validate indices
    if (result.source.index < 0 || result.source.index >= orderedNavigation.length) {
      console.warn('[Layout] Invalid source index:', result.source.index);
      return;
    }

    if (result.destination.index < 0 || result.destination.index >= orderedNavigation.length) {
      console.warn('[Layout] Invalid destination index:', result.destination.index);
      return;
    }

    // Only proceed if indices are valid and different
    if (result.source.index === result.destination.index) {
      return;
    }

    const items = Array.from(orderedNavigation.map(item => item.name));
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalItemOrder(items);
  };

  // Save changes when exiting edit mode
  const handleSaveOrder = () => {
    reorderItems(localItemOrder);
    toggleEditMode();
  };

  // Cancel edit mode and reset to saved order
  const handleCancelEdit = () => {
    setLocalItemOrder(settings.itemOrder);
    toggleEditMode();
  };

  // Handle reset to default - update local state immediately
  const handleResetToDefault = () => {
    const defaultOrder = [
      'Dashboard',
      'Users',
      'Users Map',
      'Bookings',
      'Manual Leads',
      'Services',
      'Professionals',
      'Worker Approvals',
      'Hubs',
      'Service Categories',
      'Blog Posts',
      'Blog Categories',
      'Slideshow',
      'Coupons',
      'Customer Complaints',
      'Customer Reviews',
      'Notifications',
      'Analytics',
      'Payments',
      'Referrals',
      'Audit Logs',
      // Only include Admin Access if user is super-admin
      ...(user?.role === 'super-admin' ? ['Admin Access'] : [])
    ];
    setLocalItemOrder(defaultOrder);
    resetToDefault();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="bg-background lg:flex lg:h-screen">
        {/* Mobile sidebar */}
        {/* CRITICAL: Always render droppable (even if hidden) to prevent react-beautiful-dnd errors */}
        {/* NEVER use display:none - react-beautiful-dnd needs element in DOM */}
        {/* When in edit mode, sidebar MUST be visible for react-beautiful-dnd */}
        <div
          className="fixed inset-0 z-50"
          style={{
            // NEVER use display: none - use visibility/position instead
            display: 'block',
            // Position off-screen when desktop, but keep in DOM
            position: isDesktop ? 'absolute' : 'fixed',
            left: isDesktop ? '-9999px' : 0,
            width: isDesktop ? 0 : 'auto',
            height: isDesktop ? 0 : 'auto',
            overflow: isDesktop ? 'hidden' : 'visible',
            // CRITICAL: When in edit mode, ALWAYS visible (react-beautiful-dnd requirement)
            // react-beautiful-dnd needs droppable to be visible, not just in DOM
            visibility: (settings.isEditMode && !isDesktop) || (!isDesktop && sidebarOpen) ? 'visible' : 'hidden',
            opacity: (settings.isEditMode && !isDesktop) || (!isDesktop && sidebarOpen) ? 1 : 0,
            pointerEvents: (settings.isEditMode && !isDesktop) || (!isDesktop && sidebarOpen) ? 'auto' : 'none'
          }}
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => !settings.isEditMode && setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 sm:w-72 bg-surface border-r border-border flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-white/5">
                <div className="flex items-center">
                <h1 className="text-xl font-black tracking-tight text-white italic">
                  Spotles<span className="text-muted-foreground not-italic font-light ml-1">Solutions</span>
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                {settings.isEditMode ? (
                  <>
                    <button
                      onClick={handleSaveOrder}
                      className="p-2 rounded-lg bg-primary text-black hover:bg-primary/90"
                      title="Save Order"
                    >
                      <Save className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text"
                      title="Cancel"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={toggleEditMode}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text"
                      title="Edit Sidebar"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {settings.isEditMode && (
              <div className="flex-shrink-0 px-4 py-2 bg-gray-800 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text">Drag to reorder</span>
                  <button
                    onClick={handleResetToDefault}
                    className="text-xs text-muted-foreground hover:text-text flex items-center space-x-1 transition-colors"
                    title="Reset to Default Order"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable Navigation */}
            <nav className="flex-1 overflow-y-auto mt-4">
              <Droppable
                droppableId="mobile-sidebar"
                isDropDisabled={!settings.isEditMode || isDesktop}
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={snapshot.isDraggingOver ? 'bg-gray-800/50' : ''}
                    style={{ minHeight: '100%' }}
                  >
                    {(orderedNavigation || []).map((item, index) => {
                      const Icon = item.icon;
                      const isDraggable = settings.isEditMode;

                      const navLinkClasses = `flex items-center px-4 py-2.5 my-0.5 mx-2 rounded-xl text-sm font-medium transition-all duration-200 group ${
                        isCurrentPath(item.href)
                          ? 'bg-gradient-to-r from-white/15 to-white/5 text-white shadow-lg border-l-4 border-white'
                          : 'text-muted-foreground hover:text-white hover:bg-white/5 border-l-4 border-transparent'
                      }`;

                      const navItem = (
                        <div className="relative group/nav">
                          {/* Section Header Logic: Show if category changes AND not in edit mode */}
                          {!settings.isEditMode && index > 0 && item.category !== (orderedNavigation[index - 1] as any).category && (
                            <div className="px-6 py-4 mt-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted/60">{item.category}</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-border/20 via-border/10 to-transparent" />
                              </div>
                            </div>
                          )}
                          {/* First item header */}
                          {!settings.isEditMode && index === 0 && (
                            <div className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted/60">{item.category}</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-border/20 via-border/10 to-transparent" />
                              </div>
                            </div>
                          )}

                          <div className={navLinkClasses + (isDraggable ? ' cursor-move' : ' cursor-pointer')}>
                            {isDraggable && (
                              <div className="mr-2 text-muted-foreground/50 group-hover:text-white transition-colors">
                                <GripVertical className="w-4 h-4" />
                              </div>
                            )}
                            <Icon className={`w-5 h-5 mr-3 transition-transform duration-300 group-hover:scale-110 ${isCurrentPath(item.href) ? 'text-white' : 'text-muted-foreground/70 group-hover:text-white'}`} />
                            <Link
                              to={item.href}
                              className="flex-1"
                              onClick={() => {
                                if (!settings.isEditMode) {
                                  setSidebarOpen(false);
                                }
                              }}
                            >
                              {item.name}
                            </Link>
                          </div>
                        </div>
                      );

                      if (isDraggable) {
                        return (
                          <Draggable key={`${item.name}-${index}`} draggableId={`${item.name}-${item.href}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={snapshot.isDragging ? 'opacity-50' : ''}
                              >
                                {navItem}
                              </div>
                            )}
                          </Draggable>
                        );
                      }

                      return <div key={item.name}>{navItem}</div>;
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </nav>
          </div>
        </div>

        {/* Desktop sidebar */}
        {/* CRITICAL: Always render droppable to prevent react-beautiful-dnd errors */}
        {/* When in edit mode, sidebar MUST be visible and accessible */}
        <div
          className="lg:flex-shrink-0 transition-all duration-300 ease-in-out"
          style={{
            // ALWAYS in DOM - never use display: none
            display: 'block',
            // Position off-screen when not desktop, but keep in DOM
            position: !isDesktop ? 'absolute' : 'relative',
            left: !isDesktop ? '-9999px' : 'auto',
            // Conditionally set width based on visibility - makes content extend when sidebar hidden
            // CRITICAL: Set width to 0 when hidden to allow main content to expand
            width: !isDesktop ? 0 : (isDesktop && (settings.isVisible || settings.isEditMode) ? '20rem' : 0),
            minWidth: !isDesktop ? 0 : (isDesktop && (settings.isVisible || settings.isEditMode) ? '20rem' : 0),
            maxWidth: !isDesktop ? 0 : (isDesktop && (settings.isVisible || settings.isEditMode) ? '20rem' : 0),
            // Also set padding to 0 when hidden to ensure no space is taken
            padding: !isDesktop ? 0 : (isDesktop && (settings.isVisible || settings.isEditMode) ? undefined : 0),
            margin: !isDesktop ? 0 : (isDesktop && (settings.isVisible || settings.isEditMode) ? undefined : 0),
            height: !isDesktop ? 0 : 'auto',
            overflow: !isDesktop ? 'hidden' : ((isDesktop && (settings.isVisible || settings.isEditMode)) ? 'visible' : 'hidden'),
            // CRITICAL: In edit mode on desktop, ALWAYS visible (react-beautiful-dnd requirement)
            // react-beautiful-dnd CANNOT find droppables with visibility: hidden
            // When edit mode is active, force visibility regardless of settings.isVisible
            visibility: settings.isEditMode && isDesktop
              ? 'visible'
              : (isDesktop && settings.isVisible ? 'visible' : 'hidden'),
            opacity: settings.isEditMode && isDesktop
              ? 1
              : (isDesktop && settings.isVisible ? 1 : 0),
            pointerEvents: settings.isEditMode && isDesktop
              ? 'auto'
              : (isDesktop && settings.isVisible ? 'auto' : 'none'),
            // Ensure z-index is high enough when in edit mode
            zIndex: settings.isEditMode && isDesktop ? 50 : 'auto',
            // Force layout recalculation when entering edit mode
            willChange: settings.isEditMode && isDesktop ? 'visibility, width' : 'auto'
          }}
        >
          <div
            className="flex flex-col h-screen bg-surface border-r border-border"
            style={{
              // Constrain inner container width to parent wrapper width
              width: isDesktop && (settings.isVisible || settings.isEditMode) ? '100%' : '0',
              maxWidth: isDesktop && (settings.isVisible || settings.isEditMode) ? '100%' : '0',
              minWidth: isDesktop && (settings.isVisible || settings.isEditMode) ? 'auto' : '0',
              overflow: 'hidden',
              // Ensure container collapses when wrapper is 0
              flexShrink: isDesktop && (settings.isVisible || settings.isEditMode) ? 0 : 1,
              flexBasis: isDesktop && (settings.isVisible || settings.isEditMode) ? 'auto' : '0%'
            }}
          >
            {/* Fixed Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-6 border-b border-white/5">
              <div className="flex items-center">
                <h1 className="text-xl font-black tracking-tight text-white italic">
                  Spotles<span className="text-muted-foreground not-italic font-light ml-1">Solutions</span>
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                {settings.isEditMode ? (
                  <>
                    <button
                      onClick={handleSaveOrder}
                      className="p-2 rounded-lg bg-primary text-black hover:bg-primary/90 transition-colors"
                      title="Save Order"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        // CRITICAL: Show sidebar BEFORE entering edit mode to ensure droppable is accessible
                        if (!settings.isVisible && isDesktop) {
                          toggleSidebar();
                          // Small delay to ensure DOM update before edit mode
                          setTimeout(() => {
                            toggleEditMode();
                          }, 50);
                        } else {
                          toggleEditMode();
                        }
                      }}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text transition-colors"
                      title="Edit Sidebar"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={toggleSidebar}
                      className="p-2 rounded-lg hover:bg-gray-800 text-text transition-colors"
                      title="Hide Sidebar"
                      disabled={settings.isEditMode}
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {settings.isEditMode && (
              <div className="flex-shrink-0 px-4 py-2 bg-gray-800 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text">Drag to reorder items</span>
                  <button
                    onClick={handleResetToDefault}
                    className="text-xs text-muted-foreground hover:text-text flex items-center space-x-1 transition-colors"
                    title="Reset to Default Order"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable Navigation */}
            <nav
              className="flex-1 overflow-y-auto mt-4"
              style={{
                // Ensure nav also respects width constraints
                width: '100%',
                maxWidth: '100%'
              }}
            >
              {/* CRITICAL: Droppable must ALWAYS be rendered (never conditionally) */}
              {/* react-beautiful-dnd requires droppable to always exist in DOM */}
              <Droppable
                droppableId="desktop-sidebar"
                isDropDisabled={!settings.isEditMode || !isDesktop}
                type="SIDEBAR"
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={snapshot.isDraggingOver ? 'bg-gray-800/30' : ''}
                    style={{ minHeight: '100%' }}
                  >
                    {(orderedNavigation || []).map((item, index) => {
                      const Icon = item.icon;
                      const isDraggable = settings.isEditMode;

                      const navLinkClasses = `flex items-center px-4 py-2.5 my-0.5 mx-2 rounded-xl text-sm font-medium transition-all duration-200 group ${
                        isCurrentPath(item.href)
                          ? 'bg-gradient-to-r from-white/15 to-white/5 text-white shadow-lg border-l-4 border-white'
                          : 'text-muted-foreground hover:text-white hover:bg-white/5 border-l-4 border-transparent'
                      }`;

                      const navItem = (
                        <div className="relative group/nav">
                          {/* Section Header Logic: Show if category changes AND not in edit mode */}
                          {!settings.isEditMode && index > 0 && item.category !== (orderedNavigation[index - 1] as any).category && (
                            <div className="px-6 py-4 mt-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted/60">{item.category}</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-border/20 via-border/10 to-transparent" />
                              </div>
                            </div>
                          )}
                          {/* First item header */}
                          {!settings.isEditMode && index === 0 && (
                            <div className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted/60">{item.category}</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-border/20 via-border/10 to-transparent" />
                              </div>
                            </div>
                          )}

                          <div className={navLinkClasses + (isDraggable ? ' cursor-move' : ' cursor-pointer')}>
                            {isDraggable && (
                              <div className="mr-2 text-muted-foreground/50 group-hover:text-white transition-colors">
                                <GripVertical className="w-4 h-4" />
                              </div>
                            )}
                            <Icon className={`w-5 h-5 mr-3 transition-transform duration-300 group-hover:scale-110 ${isCurrentPath(item.href) ? 'text-white' : 'text-muted-foreground/70 group-hover:text-white'}`} />
                            <Link
                              to={item.href}
                              className="flex-1"
                              onClick={(e) => {
                                if (settings.isEditMode) {
                                  e.preventDefault();
                                }
                              }}
                            >
                              {item.name}
                            </Link>
                          </div>
                        </div>
                      );

                      if (isDraggable) {
                        return (
                          <Draggable key={`${item.name}-${index}`} draggableId={`${item.name}-${item.href}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={snapshot.isDragging ? 'opacity-50 bg-gray-700 rounded' : ''}
                              >
                                {navItem}
                              </div>
                            )}
                          </Draggable>
                        );
                      }

                      return <div key={item.name}>{navItem}</div>;
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 lg:min-w-0 lg:flex lg:flex-col lg:h-screen">
          {/* Top bar */}
          <div className="sticky top-0 z-40 bg-surface border-b border-border">
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2.5 rounded-lg hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
                  aria-label="Open sidebar"
                >
                  <Menu className="w-5 h-5 text-text" />
                </button>
                {!settings.isVisible && (
                  <button
                    onClick={toggleSidebar}
                    className="hidden lg:flex p-2 rounded-lg hover:bg-gray-800 text-text transition-colors min-h-[44px] min-w-[44px]"
                    title="Show Sidebar"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                <RealtimeStatus />

                {/* System Health - Compact */}
                {!systemHealthLoading && (
                  <div className="hidden md:flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Server className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                      {getHealthIcon(health.server)}
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Database className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                      {getHealthIcon(health.database)}
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                      <span className={`text-xs sm:text-sm font-medium ${getResponseTimeColor(health.apiResponseTime)}`}>
                        {health.apiResponseTime}ms
                      </span>
                    </div>
                  </div>
                )}

                {/* Session Timer */}
                <SessionTimer />

                <div className="relative user-menu">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 p-2 sm:p-2.5 rounded-lg hover:bg-gray-800 min-h-[44px] min-w-[44px] touch-manipulation"
                    aria-label="User menu"
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-black">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-text hidden sm:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-text hidden sm:block" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-72 bg-surface border border-border rounded-lg shadow-lg">
                      <div className="py-1">
                        <div className="px-4 py-2 text-sm text-text border-b border-border">
                          <div className="font-medium truncate">{user?.name}</div>
                          <div className="text-muted-foreground truncate" title={user?.email || ''}>{user?.email}</div>
                          <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                        </div>
                        <button
                          onClick={logout}
                          className="flex items-center w-full px-4 py-2 text-sm text-text hover:bg-gray-800"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="p-4 sm:p-6 lg:overflow-y-auto lg:flex-1">
            {children}
          </main>
        </div>
      </div>
    </DragDropContext>
  );
};

export default Layout;
